/**
 * Read-only helpers over a BotView. Bots only look at what a player could see:
 * positions, HP, auras, cast bars, telegraphs, zones and (party) cooldowns.
 */
import type {
  AbilityDef,
  AbilityId,
  AuraId,
  BotView,
  DeepReadonly,
  DungeonDef,
  EnemyAbility,
  PackId,
  ResourceId,
  Unit,
  UnitId,
  Vec2,
  World,
  WorldShape,
} from '@keystone/sim';
import { MELEE_RANGE_BONUS, MIN_MELEE_RANGE, MOVING_LEEWAY } from '@keystone/sim';
import { centroid, dist } from './geometry';

export type RUnit = DeepReadonly<Unit>;
export type RWorld = DeepReadonly<World>;

/** The sim's query API takes a mutable World type; bots never mutate it. */
export function simWorld(p: { world: RWorld }): World {
  return p.world as unknown as World;
}

export function hpPct(u: RUnit): number {
  return u.maxHp > 0 ? u.hp / u.maxHp : 0;
}

export function missingHp(u: RUnit): number {
  return Math.max(0, u.maxHp - u.hp);
}

export function resource(u: RUnit, id: ResourceId): number {
  return u.resources[id]?.cur ?? 0;
}

export function resourcePct(u: RUnit, id: ResourceId): number {
  const pool = u.resources[id];
  return pool && pool.max > 0 ? pool.cur / pool.max : 0;
}

export function findAura(u: RUnit, auraId: AuraId, casterId?: UnitId) {
  return u.auras.find((a) => a.auraId === auraId && (casterId === undefined || a.casterId === casterId));
}

export function hasAura(u: RUnit, auraId: AuraId, casterId?: UnitId): boolean {
  return findAura(u, auraId, casterId) !== undefined;
}

/** Remaining ms on an aura (Infinity if permanent, 0 if missing). */
export function auraRemaining(u: RUnit, auraId: AuraId, now: number, casterId?: UnitId): number {
  const a = findAura(u, auraId, casterId);
  if (!a) return 0;
  return a.expiresAt === null ? Infinity : Math.max(0, a.expiresAt - now);
}

/** Charges available now (cooldown state is visible on party frames). */
export function charges(u: RUnit, ability: AbilityDef): number {
  const cd = u.cooldowns[ability.id];
  return cd ? cd.charges : (ability.cooldown?.charges ?? 1);
}

export function cooldownReady(u: RUnit, ability: AbilityDef): boolean {
  return charges(u, ability) > 0;
}

/**
 * +field: ClassDef.unlocks locks `abilityId` (or what a press of it resolves to) for this levelled
 * unit. Units without a level (M+ keys) have everything.
 */
export function abilityLocked(view: Pick<BotView, 'content' | 'queries'>, u: RUnit, abilityId: AbilityId): boolean {
  if (u.level === undefined || !u.classId) return false;
  const unlocks = view.content.class(u.classId)?.unlocks;
  if (!unlocks) return false;
  const lvl = u.level;
  const need = unlocks[abilityId];
  if (need !== undefined && need > lvl) return true;
  const resolved = view.queries.resolveAbility?.(u.id, abilityId);
  const needR = resolved && resolved !== abilityId ? unlocks[resolved] : undefined;
  return needR !== undefined && needR > lvl;
}

export function isHostile(a: RUnit, b: RUnit): boolean {
  return a.faction !== b.faction;
}

/** Melee range per PLAN §3 (TC formula), with moving leeway. */
export function meleeRange(a: RUnit, b: RUnit): number {
  const base = Math.max(MIN_MELEE_RANGE, a.combatReach + b.combatReach + MELEE_RANGE_BONUS);
  return a.moving && b.moving ? base + MOVING_LEEWAY : base;
}

export function inMelee(a: RUnit, b: RUnit): boolean {
  return dist(a.pos, b.pos) <= meleeRange(a, b);
}

/** Ability range check from world data (melee or edge-to-edge spell range). */
export function inAbilityRange(a: RUnit, b: RUnit, ability: AbilityDef): boolean {
  if (ability.melee || (!ability.range && ability.targeting === 'enemy')) return inMelee(a, b);
  if (!ability.range) return true;
  return dist(a.pos, b.pos) - a.combatReach - b.combatReach <= ability.range;
}

/** Is `a` standing behind `b` (outside b's 180° front). */
export function isBehind(a: RUnit, b: RUnit): boolean {
  const dx = a.pos.x - b.pos.x;
  const dy = a.pos.y - b.pos.y;
  return dx * Math.cos(b.facing) + dy * Math.sin(b.facing) < 0;
}

export function isEngaged(u: RUnit): boolean {
  return u.alive && u.faction === 'enemy' && (u.inCombat || u.ai?.mode === 'combat') && u.ai?.mode !== 'evading';
}

/** Cast progress 0..1. */
export function castProgress(u: RUnit, now: number): number {
  if (!u.cast) return 0;
  const total = Math.max(1, u.cast.endsAt - u.cast.startedAt);
  return Math.min(1, Math.max(0, (now - u.cast.startedAt) / total));
}

export function packCenter(dungeon: DungeonDef | undefined, packId: PackId): Vec2 | null {
  const p = dungeon?.packs.find((x) => x.id === packId);
  return p ? centroid(p.spawns.map((s) => s.pos)) : null;
}

export function packAlive(world: RWorld, packId: PackId): boolean {
  const st = world.packs[packId];
  if (!st) return false;
  if (st.state === 'dead') return false;
  return st.memberIds.some((id) => world.units[id]?.alive);
}

/** Snapshot of the situation for one bot at one think. */
export interface Perception {
  view: BotView;
  world: RWorld;
  now: number;
  self: RUnit;
  /** All party units in slot order (alive or not). */
  partyAll: RUnit[];
  /** Alive party units in slot order. */
  party: RUnit[];
  /** Alive enemies that are in combat with us. */
  engaged: RUnit[];
  /** Alive enemies (any state). */
  enemies: RUnit[];
  /** The party's tank (alive or not). */
  tank: RUnit | undefined;
  dungeon: DungeonDef | undefined;
  /** Enemy-sourced telegraphs and zones that can hurt us (unfiltered by reaction). */
  dangers: DangerArea[];
}

export interface DangerArea {
  key: string;
  area: DeepReadonly<WorldShape>;
  /** Visible from (telegraph createdAt / zone createdAt). */
  seenAt: number;
  /** Resolution time (telegraphs) or expiry (zones). */
  until: number;
  kind: 'telegraph' | 'zone';
}

export function perceive(view: BotView): Perception {
  const world = view.world;
  const self = world.units[view.selfId] as RUnit;
  // +personas: a member who left the group is gone (never waited for, healed or followed).
  const partyAll = world.partyIds.map((id) => world.units[id]).filter((u): u is RUnit => !!u && !u.departed);
  const party = partyAll.filter((u) => u.alive);
  const enemies: RUnit[] = [];
  for (const id of world.unitOrder) {
    const u = world.units[id];
    if (u && u.alive && u.faction === 'enemy') enemies.push(u);
  }
  const engaged = enemies.filter(isEngaged);
  const tank = partyAll.find((u) => u.role === 'tank');
  const dungeon = view.content.dungeon(world.dungeonId);
  return { view, world, now: view.now, self, partyAll, party, engaged, enemies, tank, dungeon, dangers: collectDangers(world, self) };
}

function hurtsUs(world: RWorld, sourceId: UnitId, affects: string, self: RUnit): boolean {
  const src = world.units[sourceId];
  const hostileSource = src ? src.faction !== self.faction : true;
  if (affects === 'all') return true;
  if (affects === 'enemies') return hostileSource;
  return false;
}

function collectDangers(world: RWorld, self: RUnit): DangerArea[] {
  const out: DangerArea[] = [];
  for (const t of world.telegraphs) {
    if (!hurtsUs(world, t.sourceId, t.affects, self)) continue;
    out.push({ key: `t:${t.id}`, area: t.area, seenAt: t.createdAt, until: t.resolvesAt, kind: 'telegraph' });
  }
  for (const z of world.zones) {
    if (!z.tick && !z.insideAuraId) continue;
    if (!hurtsUs(world, z.sourceId, z.affects, self)) continue;
    out.push({ key: `z:${z.id}`, area: z.area, seenAt: z.createdAt, until: z.expiresAt, kind: 'zone' });
  }
  return out;
}

export function enemyAbility(p: Perception, id: AbilityId): EnemyAbility | undefined {
  return p.view.content.enemyAbility(id);
}

/** Enemies currently casting an ability of `kind` (perceived after `reactionMs`). */
export function enemyCastsOfKind(p: Perception, kind: EnemyAbility['kind'], reactionMs: number): RUnit[] {
  return p.engaged.filter((e) => e.cast && p.now >= e.cast.startedAt + reactionMs && enemyAbility(p, e.cast.abilityId)?.kind === kind);
}

/** Who a cast is aimed at: the explicit cast target, else the caster's current victim. */
export function castTargetId(u: RUnit): UnitId | null {
  return u.cast?.targetId ?? u.targetId ?? null;
}

/** Engaged enemies within `radius` of a point. */
export function enemiesNear(p: Perception, at: Vec2, radius: number): number {
  let n = 0;
  for (const e of p.engaged) if (dist(e.pos, at) <= radius + e.radius) n++;
  return n;
}
